/* eslint-disable jira/filename-case */

import { autoBeforeEachTest } from '@jira/platform__utils-auto-before-each-test/src/auto-before-each-test.tsx';
import defaults from '../defaults.tsx';
import type {
	Action,
	ActionThunk,
	BoundActions,
	StoreConfig,
	StoreInstance,
	StoreState,
} from '../types.tsx';

const namedMutator =
	(
		// storeState should be StoreState<TState>
		// but this mutator's signature doesn't match
		// the mutator's actual type of (Partial<TState>) => void
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		storeState: any,
		actionName: string,
	) =>
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	(...arg: any[]) => {
		// oxlint-disable-next-line no-param-reassign
		storeState.mutator.actionName = actionName;
		return storeState.mutator(...arg);
	};

let warnings = new WeakMap();
autoBeforeEachTest(() => {
	warnings = new WeakMap();
});

export const bindAction = <TState, TActions extends Record<string, ActionThunk<TState, TActions>>>(
	storeState: StoreState<TState>,
	actionFn: ActionThunk<TState, TActions>,
	actionKey: string,
	config: StoreConfig,
	actions: BoundActions<TState, TActions>,
) => {
	const callThunk = (
		instance: StoreInstance<TState, TActions>,
		thunkFn: Action<TState>,
		actionName: string,
	) =>
		thunkFn(
			{
				setState: defaults.devtools
					? namedMutator(instance.storeState, actionName)
					: instance.storeState.mutator,
				getState: instance.storeState.getState,
				// oxlint-disable-next-line typescript/ban-ts-comment
				// @ts-ignore - TS2353 actions() is a deprecated API
				get actions() {
					if (!warnings.has(actionFn)) {
						warnings.set(
							actionFn,
							// oxlint-disable-next-line no-console
							console.warn(
								"react-sweet-state 'actions' property has been deprecated and will be removed in the next mayor. " +
									`Please check action '${actionName}' of Store '${instance.storeState.key}' and use 'dispatch' instead`,
							),
						);
					}

					return actions;
				},
				// oxlint-disable-next-line @typescript-eslint/no-explicit-any
				dispatch: (tFn: Action<TState, any, any>): ReturnType<typeof tFn> =>
					callThunk(instance, tFn, `${actionName}.dispatch`),
			},
			config.props(),
		);
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	return (...args: any[]): void | Promise<void> =>
		callThunk({ storeState, actions }, actionFn(...args), actionKey);
};

export const bindActions = <TState, TActions extends Record<string, ActionThunk<TState, TActions>>>(
	actions: TActions,
	storeState: StoreState<TState>,
	config: StoreConfig,
	boundActions: BoundActions<TState, TActions> | null = null,
): BoundActions<TState, TActions> =>
	// oxlint-disable-next-line typescript/consistent-type-assertions
	(Object.keys(actions) as (keyof TActions)[]).reduce(
		(acc, k) => {
			// oxlint-disable-next-line typescript/consistent-type-assertions
			acc[k] = bindAction(
				storeState,
				actions[k],
				// oxlint-disable-next-line typescript/consistent-type-assertions
				k as string,
				config,
				boundActions || acc,
			) as BoundActions<TState, TActions>[keyof TActions];
			return acc;
		},
		// oxlint-disable-next-line typescript/consistent-type-assertions
		{} as BoundActions<TState, TActions>,
	);
